'use client'
import { useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IAuthResponse, IUser, ILoginRequest, IPairRequest, IPairResponse, IRegisterRequest, IFirebaseSsoRequest, ISsoRequest, IChangePasswordRequest, IRecoverPasswordRequest } from '@/types/auth'
import { IError, AppError, getErrorMessage } from '@/types/error'
import { useRecaptcha } from '@/hooks/utils/useRecaptcha'
import { useTagManager } from '@/hooks/analytics/useTagManager'
import { useDevice } from '@/hooks/utils/useDevice'
import { resetUserScopedQueries } from '@/utils/query/resetUserScopedQueries'
import { useActivationEntitlement } from '@/hooks/activation/useActivationEntitlement'
import { logout } from '@/services/logout'
import { AUTH_FLOW_MUTATION_KEY } from '@/constants/auth'
import { IProfile } from '@/types'

import settings from '@/settings/public'

type SignInActionType = 'login' | 'register'

const requestHistory: number[] = [];
const MAX_REQUESTS = 5;
const WINDOW_MS = 60 * 1000; // 1 minute

const login = async (data: ILoginRequest, device: string): Promise<IAuthResponse> => {
  const res = await fetch(`${settings.api.host}/api/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ ...data, device }),
  })

  const response = await res.json()

  if (response.error) {
    throw response.error as IError
  }

  return response
}

/**
 * Register keeps the request context (`data`, `device`) so onSuccess can
 * replay it as the auto-login; the backend reply stays enveloped under
 * `response.data` (the register proxy does not unwrap it like the other
 * three). Typed so the `orderLinked` adapter in the mutation breaks loudly
 * if this envelope ever changes shape.
 */
interface IRegisterResult {
  data: IRegisterRequest
  device: string
  response: { data: IAuthResponse }
}

const register = async (data: IRegisterRequest, device: string): Promise<IRegisterResult> => {
  const res = await fetch(`${settings.api.host}/api/auth/register`, {
    method: 'POST',
    body: JSON.stringify({ ...data, device }),
  })

  const response = await res.json()

  if (response.error) {
    throw response.error as IError
  }

  return { data, device, response }
}

const authenticateWithFirebase = async (data: IFirebaseSsoRequest, device: string): Promise<IAuthResponse> => {
  const res = await fetch(`${settings.api.host}/api/auth/firebase`, {
    method: 'POST',
    body: JSON.stringify({ ...data, device }),
  })

  const response = await res.json()

  if (response.error) {
    throw response.error as IError
  }

  return response
}

const authenticateWithSso = async (data: ISsoRequest, device: string): Promise<IAuthResponse> => {
  const res = await fetch(`${settings.api.host}/api/auth/sso`, {
    method: 'POST',
    body: JSON.stringify({ ...data, device }),
  })

  const response = await res.json()

  if (response.error) {
    throw response.error as IError
  }

  return response
}

const recoverPassword = async (data: any) => {
  const now = Date.now()

  // 1. Filter the history to keep only the attempts from the last minute
  const recentRequests = requestHistory.filter(timestamp => now - timestamp < WINDOW_MS)

  // 2. Update the history
  requestHistory.length = 0
  requestHistory.push(...recentRequests)

  // 3. Validate the limit
  if (requestHistory.length >= MAX_REQUESTS) {
    throw {
      code: 429,
      extra: {},
      userMessage: {
        original: 'Too many attempts',
        es: 'Demasiados intentos',
        pt: 'Muitas tentativas'
      },
    } as IError
  }

  // 4. Proceed with the password recovery request
  const res = await fetch(`${settings.api.host}/api/1.0/auth/users/password/recover`, {
    method: 'POST',
    body: JSON.stringify({ data }),
  })

  // 5. Register the successful attempt (or failed, according to your security policy)
  requestHistory.push(Date.now())

  const response = await res.json()

  if (response.error) {
    throw response.error as IError
  }

  return response
}

const changePassword = async (data: IChangePasswordRequest): Promise<IProfile> => {
  const res = await fetch(`${settings.api.host}/api/1.0/auth/users/password/recover/change`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  })

  const response = await res.json()

  if (response.error) {
    throw response.error
  }

  return response
}

const pair = async (data: IPairRequest): Promise<IPairResponse> => {
  const res = await fetch(`${settings.api.host}/api/1.0/auth/pair/validate`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

  const response = await res.json()

  if (response.error) {
    throw response.error as IError
  }

  return response
}

export const useAuth = () => {
  const queryClient = useQueryClient()
  const { device } = useDevice()

  const { withRecaptchaRetry } = useRecaptcha()
  const { sendGtm } = useTagManager()
  const { withEntitlement } = useActivationEntitlement()

  // Bumped on every successful authentication. A logout that started before a
  // fresh login must not, when its (possibly stalled) response finally lands,
  // clear the NEW session's client state. logoutMutation captures this value at
  // start and skips its teardown if it changed meanwhile. See PRO-344 D2.
  const authGenerationRef = useRef(0)

  const onAuthenticated = ({ provider, user }: {
    provider: string
    user?: IUser
  }) => {
    authGenerationRef.current += 1
    sessionStorage.setItem('auth_provider', provider)
    queryClient.invalidateQueries()
    sendGtm.USER_AUTH_SUCCEEDED({ provider, user })
  }

  const loginMutation = useMutation({
    mutationKey: [...AUTH_FLOW_MUTATION_KEY, 'login'],
    mutationFn: async (data: ILoginRequest) => {
      return withEntitlement((entitlementId) =>
        withRecaptchaRetry('login', (recaptchaToken) => login({ ...data, entitlementId, recaptchaToken }, device)),
      { source: 'login' })
    },
    onSuccess: (data) => {
      onAuthenticated({ provider: 'email', user: data.user })
    },
    onError: (e: AppError) => {
      sendGtm.USER_AUTH_FAILED({ provider: 'email', action: 'login', error: getErrorMessage(e) })
    }
  })

  const registerMutation = useMutation({
    mutationKey: [...AUTH_FLOW_MUTATION_KEY, 'register'],
    mutationFn: async (data: IRegisterRequest) => {
      return withEntitlement(
        (entitlementId) => withRecaptchaRetry('register', (recaptchaToken) => register({ ...data, entitlementId, recaptchaToken }, device)),
        { source: 'register', readLinkingFields: (result) => result.response.data }
      )
    },
    onSuccess: async ({ data, response }) => {
      sessionStorage.setItem('is_new_user', 'true')
      sendGtm.USER_REGISTER_SUCCEEDED({ provider: 'email', user: response.data.user })

      try {
        // Wrapped in withEntitlement on its own: when register consumed the
        // id, getEntitlementId() is null and the explicit `entitlementId`
        // key overrides the stale copy sitting in the retained form `data`;
        // when register reported `orderLinked: false`, the kept record rides
        // again here and this wrapper is what consumes it if the retry links.
        const loginResponse = await withEntitlement((entitlementId) =>
          withRecaptchaRetry('login', (recaptchaToken) => login({ ...data, entitlementId, recaptchaToken }, device)),
        { source: 'register-auto-login' })
        onAuthenticated({ provider: 'email', user: loginResponse.user })
      } catch (e) {
        // The account exists at this point — only the auto-login failed, so
        // without this the outcome reports as USER_REGISTER_FAILED, which is
        // the wrong story. reCAPTCHA makes this reachable in a new way: a
        // second token minted seconds after the first is exactly when v3
        // scores dip, and a low score is not retryable.
        sendGtm.USER_AUTH_FAILED({ provider: 'email', action: 'login', error: getErrorMessage(e as AppError) })
        throw e
      }
    },
    onError: (e: AppError) => {
      sendGtm.USER_REGISTER_FAILED({ provider: 'email', error: getErrorMessage(e) })
    }
  })

  const firebaseSsoMutation = useMutation({
    // Backend-side this is the same login/register handler pair as the email
    // mutations (providerBasedLogin/firebase chains into them), so the
    // activation id links here too — this is the path a Google/Apple sign-in
    // on /entel takes.
    mutationKey: [...AUTH_FLOW_MUTATION_KEY, 'firebase'],
    mutationFn: (data: IFirebaseSsoRequest) =>
      withEntitlement((entitlementId) => authenticateWithFirebase({ ...data, entitlementId }, device), { source: 'firebase' }),
    onSuccess: (data) => {
      const signInAction = data.signInAction as SignInActionType

      if (signInAction === 'register') {
        sessionStorage.setItem('is_new_user', 'true')
        sendGtm.USER_REGISTER_SUCCEEDED({ provider: 'firebase', user: data.user })
      }

      onAuthenticated({ provider: 'firebase', user: data.user })
    },
    onError: (e: AppError) => {
      sendGtm.USER_AUTH_FAILED({ provider: 'firebase', error: getErrorMessage(e) })
    }
  })

  const ssoMutation = useMutation({
    mutationKey: [...AUTH_FLOW_MUTATION_KEY, 'sso'],
    mutationFn: async (data: ISsoRequest) => {
      return withEntitlement((entitlementId) =>
        withRecaptchaRetry('sso', (recaptchaToken) => authenticateWithSso({ ...data, entitlementId, recaptchaToken }, device)),
      // /login/sso ignores the field on fx-npm-auth 2.4.0 — this tag is what
      // keeps its by-design warning separable from a real deploy gap.
      { source: 'sso' })
    },
    onSuccess: (data, variables) => {
      const signInAction = data.signInAction as SignInActionType
      const provider = variables.provider || 'sso'

      if (signInAction === 'register') {
        sessionStorage.setItem('is_new_user', 'true')
        sendGtm.USER_REGISTER_SUCCEEDED({ provider, user: data.user })
      }

      onAuthenticated({ provider, user: data.user })
    },
    onError: (e: AppError) => {
      sendGtm.USER_AUTH_FAILED({ provider: 'sso', error: getErrorMessage(e) })
    }
  })

  const recoverPasswordMutation = useMutation({
    mutationFn: async (data: IRecoverPasswordRequest) => {
      return withRecaptchaRetry('password_recover', (recaptchaToken) => recoverPassword({ ...data, recaptchaToken }))
    },
    onError: (e: AppError) => {
      sendGtm.USER_AUTH_FAILED({ action: 'recover_password', error: getErrorMessage(e) })
    }
  })

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
    onError: (e: AppError) => {
      sendGtm.USER_AUTH_FAILED({ action: 'change_password', error: getErrorMessage(e) })
    }
  })

  const pairMutation = useMutation({
    mutationFn: pair,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
    onError: (e: AppError) => {
      sendGtm.USER_AUTH_FAILED({ action: 'pair', error: getErrorMessage(e) })
    }
  })

  const logoutMutation = useMutation({
    mutationFn: logout,
    onMutate: () => ({ generation: authGenerationRef.current }),
    onSuccess: (_data, _vars, context) => {
      sendGtm.USER_LOGOUT_COMPLETED({})
      // A login landed after this logout started — its session is the current
      // one; don't clobber it with this stale logout's teardown.
      if (context?.generation !== authGenerationRef.current) return
      sessionStorage.clear()
      resetUserScopedQueries(queryClient)
    },
    onError: (e: AppError & { serverResponded?: boolean }, _vars, context) => {
      sendGtm.USER_AUTH_FAILED({ action: 'logout', error: getErrorMessage(e) })

      // Only when the server answered. That case used to resolve as a false
      // success, so clearing here keeps the visible outcome it always had.
      // A network failure is the opposite case: the old code cleared nothing,
      // and wiping the cache would show a signed-out UI over a session that is
      // still live server-side — the user reconnects and is logged in again.
      if (!e?.serverResponded) return

      // Same stale-logout guard as onSuccess.
      if (context?.generation !== authGenerationRef.current) return
      sessionStorage.clear()
      resetUserScopedQueries(queryClient)
    }
  })

  return {
    recoverPassword: recoverPasswordMutation.mutate,
    isPendingRecoverPassword: recoverPasswordMutation.isPending,
    recoverPasswordMutation,
    changePassword: changePasswordMutation.mutate,
    isPendingChangePassword: changePasswordMutation.isPending,
    changePasswordMutation,
    pair: pairMutation.mutate,
    isPendingPair: pairMutation.isPending,
    pairMutation,
    login: loginMutation.mutateAsync,
    isPendingLogin: loginMutation.isPending,
    loginMutation,
    register: registerMutation.mutate,
    isPendingRegister: registerMutation.isPending,
    registerMutation,
    firebaseSso: firebaseSsoMutation.mutate,
    isPendingFirebaseSso: firebaseSsoMutation.isPending,
    firebaseSsoMutation: firebaseSsoMutation,
    sso: ssoMutation.mutate,
    isPendingSso: ssoMutation.isPending,
    ssoMutation,
    logout: logoutMutation.mutate,
    isPendingLogout: logoutMutation.isPending,
    logoutMutation
  }
}